import React, { useState } from 'react';
import { MonthAbbrev, MONTH_ORDER, ReportFilters, getDefaultFilters } from '../../types/types';

interface ReportGenerationModalProps {
  onClose: () => void;
  onGenerate: (filters: ReportFilters) => void | Promise<void>;
  /** Defaults to the current year and the five years before it. */
  availableYears?: number[];
}

const MODES: ReportFilters['mode'][] = ['year', 'month', 'both'];

export function ReportGenerationModal({
  onClose,
  onGenerate,
  availableYears,
}: ReportGenerationModalProps) {
  const defaults = getDefaultFilters();
  const [mode, setMode] = useState<ReportFilters['mode']>(defaults.mode);
  const [year, setYear] = useState<number>(defaults.year);
  const [month, setMonth] = useState<MonthAbbrev>(defaults.month as MonthAbbrev);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const years = availableYears ?? Array.from({ length: 6 }, (_, i) => defaults.year - i);

  const handleGenerate = async () => {
    setError(null);
    setIsGenerating(true);
    try {
      await onGenerate({
        mode,
        year,
        month: mode === 'year' ? undefined : month,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong generating the report.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-modal-title"
    >
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
        <h2 id="report-modal-title" className="text-base font-medium text-stone-900">
          Generate report
        </h2>
        <p className="mt-1 text-xs text-stone-500">Choose the period this report should cover.</p>

        <div className="mt-4 flex rounded-md border border-stone-200 p-1">
          {MODES.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              aria-pressed={mode === m}
              className={`flex-1 rounded px-2 py-1.5 text-xs font-medium capitalize transition-colors ${
                mode === m ? 'bg-[#C2410C] text-white' : 'text-stone-600 hover:bg-stone-50'
              }`}
            >
              {m}
            </button>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <label className="text-xs text-stone-500">
            Year
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5 text-sm text-stone-900"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>

          <label className={`text-xs text-stone-500 ${mode === 'year' ? 'opacity-40' : ''}`}>
            Month
            <select
              value={month}
              onChange={(e) => setMonth(e.target.value as MonthAbbrev)}
              disabled={mode === 'year'}
              className="mt-1 w-full rounded-md border border-stone-200 px-2 py-1.5 text-sm text-stone-900 disabled:bg-stone-50"
            >
              {MONTH_ORDER.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error && <p className="mt-3 text-xs text-red-700">{error}</p>}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isGenerating}
            className="rounded-md px-3 py-1.5 text-sm text-stone-600 hover:bg-stone-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={isGenerating}
            className="rounded-md bg-[#C2410C] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#9A3412] disabled:opacity-60"
          >
            {isGenerating ? 'Generating…' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
}
